import { supabase, isSupabaseConfigured } from '../lib/supabase';

export const CERT_LAYOUT_MARKER = 'data-cert-layout="2026-09"';

export interface CertificateTemplate {
  id: string;
  name: string;
  htmlBody: string;
  isDefault: boolean;
}

function mapRow(row: any): CertificateTemplate {
  return {
    id: row.id,
    name: row.name,
    htmlBody: row.html_body,
    isDefault: Boolean(row.is_default),
  };
}

function hasCertLayoutMarker(html: string): boolean {
  return (
    html.includes(CERT_LAYOUT_MARKER) || html.includes("data-cert-layout='2026-09'")
  );
}

/** Inject the layout marker so a saved custom template is not overwritten on next seed. */
export function ensureCertLayoutMarker(html: string): string {
  if (hasCertLayoutMarker(html)) return html;
  const withPage = html.replace(
    /(<div\b[^>]*\bclass=["'][^"']*\bcertificate-page\b[^"']*["'][^>]*)(>)/i,
    `$1 ${CERT_LAYOUT_MARKER}$2`
  );
  if (withPage !== html) return withPage;
  const withBody = html.replace(/<body\b([^>]*)>/i, `<body$1 ${CERT_LAYOUT_MARKER}>`);
  if (withBody !== html) return withBody;
  return `${html}\n<!-- ${CERT_LAYOUT_MARKER} -->`;
}

/** True when stored HTML is still the pre-2026-09 layout (one-time overwrite). */
function templateNeedsRefresh(html: string): boolean {
  return !hasCertLayoutMarker(html) || html.includes('/certificates/');
}

export const certificateTemplateService = {
  async list(): Promise<CertificateTemplate[]> {
    if (!isSupabaseConfigured()) return [];
    const { data, error } = await supabase
      .from('certificate_templates')
      .select('*')
      .order('name');
    if (error) {
      console.error('certificate_templates list:', error);
      return [];
    }
    return (data || []).map(mapRow);
  },

  async getDefault(): Promise<CertificateTemplate | null> {
    if (!isSupabaseConfigured()) return null;
    const { data, error } = await supabase
      .from('certificate_templates')
      .select('*')
      .eq('is_default', true)
      .maybeSingle();
    if (error) {
      console.error('certificate_templates getDefault:', error);
      return null;
    }
    return data ? mapRow(data) : null;
  },

  async ensureDefaultSeeded(): Promise<CertificateTemplate> {
    const existing = await this.getDefault();
    // Lazy import avoids a circular dependency with certificateHtmlTemplate.ts
    const { DEFAULT_CERTIFICATE_HTML } = await import('../utils/certificateHtmlTemplate');
    const htmlBody = DEFAULT_CERTIFICATE_HTML;

    if (existing?.htmlBody) {
      if (templateNeedsRefresh(existing.htmlBody) && isSupabaseConfigured()) {
        await supabase
          .from('certificate_templates')
          .update({
            html_body: htmlBody,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
        return { ...existing, htmlBody };
      }
      return existing;
    }

    if (!isSupabaseConfigured()) {
      return {
        id: 'local-default',
        name: 'Default Certificate',
        htmlBody,
        isDefault: true,
      };
    }

    await supabase.from('certificate_templates').update({ is_default: false }).eq('is_default', true);

    const id = crypto.randomUUID();
    const { data, error } = await supabase
      .from('certificate_templates')
      .insert({
        id,
        name: 'Default Certificate',
        html_body: htmlBody,
        is_default: true,
      })
      .select('*')
      .single();

    if (error) {
      const again = await this.getDefault();
      if (again) return again;
      throw error;
    }
    return mapRow(data);
  },

  async create(template: Omit<CertificateTemplate, 'id'> & { id?: string }): Promise<CertificateTemplate> {
    if (!isSupabaseConfigured()) throw new Error('Database is not configured');
    const id = template.id || crypto.randomUUID();
    if (template.isDefault) {
      await supabase.from('certificate_templates').update({ is_default: false }).eq('is_default', true);
    }
    const { data, error } = await supabase
      .from('certificate_templates')
      .insert({
        id,
        name: template.name,
        html_body: ensureCertLayoutMarker(template.htmlBody),
        is_default: template.isDefault,
      })
      .select('*')
      .single();
    if (error) throw error;
    return mapRow(data);
  },

  async update(template: CertificateTemplate): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Database is not configured');
    if (template.isDefault) {
      await supabase
        .from('certificate_templates')
        .update({ is_default: false })
        .eq('is_default', true)
        .neq('id', template.id);
    }
    const { error } = await supabase
      .from('certificate_templates')
      .update({
        name: template.name,
        html_body: ensureCertLayoutMarker(template.htmlBody),
        is_default: template.isDefault,
        updated_at: new Date().toISOString(),
      })
      .eq('id', template.id);
    if (error) throw error;
  },

  async remove(id: string): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Database is not configured');
    const { error } = await supabase.from('certificate_templates').delete().eq('id', id);
    if (error) throw error;
  },
};
